import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { defaultInvalidateQueryFilters, queryKeys } from "../../api/query";
import { useJourneyTypes } from "../../api/queries/enum/useJourneyTypes";
import { Panel } from "primereact/panel";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useInstitutions } from "../../api/queries/institutions/useInstitutions";
import { InputText } from "primereact/inputtext";
import {
  CreatePlacementEnvironmentTemplatePayload,
  UpdatePlacementEnvironmentTemplatePayload,
} from "../../api/types/placement-environment-template";
import { useCreatePlacementEnvironmentTemplate } from "../../api/queries/placement-environment-template/useCreatePlacementEnvironmentTemplate";
import { useUpdatePlacementEnvironmentTemplate } from "../../api/queries/placement-environment-template/useUpdatePlacementEnvironment";
import { usePlacementEnvironmentTemplate } from "../../api/queries/placement-environment-template/usePlacementEnvironmentTemplate";
import { useDeletePlacementEnvironmentTemplate } from "../../api/queries/placement-environment-template/useDeletePlacementEnvironmentTemplate";

export default function PlacementEnvironmentTemplateForm() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createPlacementEnvironmentTemplate =
    useCreatePlacementEnvironmentTemplate();
  const updatePlacementEnvironmentTemplate =
    useUpdatePlacementEnvironmentTemplate(uuid);
  const deletePlacementEnvironmentTemplate =
    useDeletePlacementEnvironmentTemplate(uuid);
  const { data: placementEnvironmentTemplateResponse } =
    usePlacementEnvironmentTemplate(uuid);
  const { data: journeyTypes } = useJourneyTypes();

  const [name, setName] = useState<string>("");
  const onNameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setName(event.target.value);

  const [description, setDescription] = useState<string>("");
  const onDescriptionChange = (event: ChangeEvent<HTMLInputElement>) =>
    setDescription(event.target.value);

  const [journeyType, setJourneyType] = useState<string>("");
  const onJourneyTypeChange = (event: DropdownChangeEvent): void =>
    setJourneyType(event.value);

  const [institutions, setInstitutions] = useState<string[]>([]);
  const onInstitutionsChange = (event: MultiSelectChangeEvent): void =>
    setInstitutions(event.value);

  const { data: institutionsResponse } = useInstitutions({
    offset: 0,
    limit: 10,
    "fContextId[]": journeyType || undefined,
  });

  const onSubmit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();

    if (uuid) {
      const payload: UpdatePlacementEnvironmentTemplatePayload = {
        name,
        description,
        institutions,
      };

      await updatePlacementEnvironmentTemplate.mutateAsync(payload);

      queryClient.invalidateQueries(
        queryKeys.placementEnvironmentTemplate(uuid),
        defaultInvalidateQueryFilters,
      );
    } else {
      const payload: CreatePlacementEnvironmentTemplatePayload = {
        name,
        description,
        context: journeyType,
        institutions,
      };

      await createPlacementEnvironmentTemplate.mutateAsync(payload);
    }

    queryClient.invalidateQueries(
      queryKeys.placementEnvironmentTemplateList(),
      defaultInvalidateQueryFilters,
    );

    navigate("/journey-tracker-templates");
  };

  const onDelete = async () => {
    await deletePlacementEnvironmentTemplate.mutateAsync();

    queryClient.invalidateQueries(
      queryKeys.placementEnvironmentTemplateList(),
      defaultInvalidateQueryFilters,
    );

    navigate("/journey-tracker-templates");
  };

  useEffect(() => {
    if (placementEnvironmentTemplateResponse) {
      const { name, description, context, institutions } =
        placementEnvironmentTemplateResponse.data;

      setName(name);
      setDescription(description);
      setJourneyType(context.id);
      setInstitutions(institutions.map((i) => i.id));
    }
  }, [placementEnvironmentTemplateResponse]);

  return (
    <form onSubmit={onSubmit} className="col-12">
      <div className="p-fluid">
        <Panel
          header="Journey Tracker Template"
          toggleable={true}
          className="mb-3"
        >
          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={name} onChange={onNameChange} />
              <label>Name</label>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={description} onChange={onDescriptionChange} />
              <label>Description</label>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <Dropdown
                value={journeyType}
                onChange={onJourneyTypeChange}
                options={journeyTypes?.data.results || []}
                optionLabel="name"
                optionValue="id"
                disabled={Boolean(uuid)}
              />
              <label>Context *</label>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <MultiSelect
                options={institutionsResponse?.data.results || []}
                value={institutions}
                onChange={onInstitutionsChange}
                optionLabel="name"
                optionValue="id"
              />
              <label>Institutions</label>
            </div>
          </div>
        </Panel>
      </div>

      <div className="flex gap-1 justify-content-end">
        {uuid && (
          <Button
            type="button"
            label="Delete"
            className="p-button-danger p-button-sm"
            onClick={onDelete}
          />
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
          disabled={!journeyType}
        />
      </div>
    </form>
  );
}
